import { getUser, resetUserSession } from '~src/user';
import { getAuth0Client } from './getAuth0Client';
import { clearOfcAccess } from './ofcAccess';
import {
  isAuth0SessionError,
  isBusinessRejection,
  showAccessErrorModal,
} from './sessionRecovery';
import isMobile from 'is-mobile';
import Swal from 'sweetalert2';

const REDIRECT_CALLBACK = (redirectUrl = process.env.ROUTE_START) => {
  const normalizedRedirectUrl = redirectUrl.startsWith('/')
    ? redirectUrl
    : `/${redirectUrl}`;
  window.location.href = window.location.origin + normalizedRedirectUrl;
};

const resolveRedirect = (userContext) => {
  const { isOrgNew, editorCanvas } = userContext || {};

  let redirectPath;
  let query;

  const rawAuthRedirect = localStorage.getItem('authRedirect');

  if (rawAuthRedirect && typeof rawAuthRedirect === 'string') {
    try {
      const authRedirect = JSON.parse(rawAuthRedirect);
      redirectPath = authRedirect.url;
      query = authRedirect.queryString;
      localStorage.removeItem('authRedirect');
    } catch (err) {
      console.error(err);
    }
  }

  if (isOrgNew) {
    if (query) {
      query = `${query}&welcome=true`;
    } else {
      query = 'welcome=true';
    }
  }

  // From mobile, we display a banner asking to contact us
  if (isMobile()) {
    redirectPath = process.env.ROUTE_START;
  } else if (query && editorCanvas && editorCanvas.objectId) {
    redirectPath = `${process.env.ROUTE_CANVAS}/${editorCanvas.objectId}?${query}`;
  }

  let redirectUrl = redirectPath || process.env.ROUTE_START;

  if (query && !redirectUrl.includes('?')) {
    redirectUrl = `${redirectUrl}?${query}`;
  }

  return redirectUrl;
};

// Step 1: exchange the one-shot Auth0 callback. Only Auth0 can fail here; the
// code/state pair cannot be replayed, so a failure must never trigger Parse.
const completeAuth0Callback = (auth0) => auth0.handleRedirectCallback();

// Step 2: bootstrap the application user. This is the only place that talks to
// Parse during the callback, and it can be retried on its own.
// Never reuse state or tokens left by a previous account. `resetUserSession`
// keeps the local Parse token when server revocation fails, so a valid
// credential is only dropped after revocation is confirmed. An Auth0 login
// also leaves the Argo service-account mode (PE-2215): its session token is
// revoked like any previous one.
const resetPreviousSession = async () => {
  clearOfcAccess();
  await resetUserSession({ revoke: true });
};

const bootstrapAuthenticatedUser = async () => {
  await resetPreviousSession();
  return getUser({}, true);
};

// The Auth0 authorization could not be established. Start a brand new
// authorization flow: /login runs `loginWithRedirect`, which is safe even when
// Auth0 still appears authenticated (it bounces back with a fresh code) and
// preserves the intended destination stored in `authRedirect`.
const handleAuth0EstablishmentError = async () => {
  const result = await showAccessErrorModal({ business: false });

  if (result.dismiss === Swal.DismissReason.cancel) {
    REDIRECT_CALLBACK(process.env.ROUTE_LOGOUT);
    return;
  }

  if (result.isConfirmed) {
    REDIRECT_CALLBACK(process.env.ROUTE_LOGIN);
  }
};

const handleBootstrapError = async (error) => {
  // A failed Auth0 token/session lookup after the callback can only be fixed by
  // restarting the authorization, not by retrying Parse.
  if (isAuth0SessionError(error)) {
    await handleAuth0EstablishmentError();
    return;
  }

  const business = isBusinessRejection(error);
  const result = await showAccessErrorModal({ business });

  if (result.dismiss === Swal.DismissReason.cancel) {
    REDIRECT_CALLBACK(process.env.ROUTE_LOGOUT);
    return;
  }

  if (!result.isConfirmed) {
    return;
  }

  if (business) {
    // Acknowledging a rejected account must leave the one-shot callback route.
    // /login would immediately bounce back to /auth while the Auth0 session is
    // still active, so end the session first, like "Cambia account" does.
    REDIRECT_CALLBACK(process.env.ROUTE_LOGOUT);
    return;
  }

  // Only a deliberate click retries bootstrap. The Auth0 callback code is not
  // replayed and a valid Parse session survives until revocation is confirmed.
  try {
    await resetPreviousSession();
    const context = await getUser({}, true);
    REDIRECT_CALLBACK(resolveRedirect(context));
  } catch (retryError) {
    await handleBootstrapError(retryError);
  }
};

export const handleAuthCallback = async () => {
  const auth0 = await getAuth0Client();

  const isAuthenticated = await auth0.isAuthenticated();

  const query = window.location.search;

  if (query.includes('code=') && query.includes('state=')) {
    try {
      await completeAuth0Callback(auth0);
    } catch (error) {
      await handleAuth0EstablishmentError();
      return;
    }

    try {
      const userContext = await bootstrapAuthenticatedUser();

      REDIRECT_CALLBACK(resolveRedirect(userContext));
    } catch (error) {
      await handleBootstrapError(error);
    }

    return;
  }

  if (isAuthenticated) {
    REDIRECT_CALLBACK();
    return;
  }

  window.location.href = process.env.ROUTE_LOGIN;
};
